import React from "react";
import { Microscope, Timer, ShieldCheck, Beaker, Users, Zap } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhyTrustOurLab() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const features = [{
    title: "Rigorous Testing",
    description: "We don't just read documentation. Our technical reviewers manually test every AI tool, building real-world projects to verify claims.",
    icon: <Microscope className="w-8 h-8 stroke-1" />
  }, {
    title: "Performance Benchmarks",
    description: "Speed and reliability matter. We measure response times, uptime, and generation quality to ensure tools meet professional standards.",
    icon: <Timer className="w-8 h-8 stroke-1" />
  }, {
    title: "Unbiased Analysis",
    description: "Our editorial integrity is paramount. Rankings are based on merit and performance data, never on paid placements or sponsorship.",
    icon: <ShieldCheck className="w-8 h-8 stroke-1" />
  }, {
    title: "Expert-Led Research",
    description: "Content is curated by industry veterans with over a decade of experience in automation and software testing.",
    icon: <Users className="w-8 h-8 stroke-1" />
  }, {
    title: "Real-World Scenarios",
    description: "We simulate actual business use cases to see how these tools perform under pressure in production environments.",
    icon: <Beaker className="w-8 h-8 stroke-1" />
  }, {
    title: "Future-Proof Insights",
    description: "Focused on the 2026 landscape, we prioritize tools that are innovating and staying ahead of the rapid AI evolution curve.",
    icon: <Zap className="w-8 h-8 stroke-1" />
  }];
  return <section id="why-trust-our-lab" ref={ref} className={`dark bg-[#0A0A0A] text-foreground py-24 px-6 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight mb-4">
            Why Trust Our Lab?
          </h2>
          <p className="text-muted-foreground text-lg font-light">
            We strip away the hype to deliver precise, data-driven insights for
            professionals who demand the best.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, i) => <div key={i} data-index={i} className="h-full">
              <Card className="h-full bg-transparent border border-border/40 shadow-none rounded-none hover:border-primary/50 transition-colors duration-300">
                <CardHeader className="pb-2">
                  <div className="mb-4 text-primary/80">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl font-medium tracking-tight">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground/80 text-base leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}